import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_CONSTANTS } from './constants';
import { Card, Button } from './components/ui';
import { tokens } from './styles/tokens';
import { useResponsive } from './hooks/useResponsive';
import responsiveStyles from './styles/responsive.module.css';

// Helper to convert decimal odds to American odds
function decimalToAmerican(decimal) {
  if (!decimal || decimal === 0) return '';
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`;
  } else {
    return `-${Math.round(100 / (decimal - 1))}`;
  }
}

// Team logo mapping
const teamLogoMapping = {
  // American League East
  "New York Yankees": "yankees.png",
  "Boston Red Sox": "redSox.png",
  "Tampa Bay Rays": "rays.png",
  "Toronto Blue Jays": "blueJays.png",
  "Baltimore Orioles": "orioles.png",
  
  // American League Central  
  "Chicago White Sox": "whiteSox.png",
  "Detroit Tigers": "tigers.png",
  "Kansas City Royals": "royals.png",
  "Minnesota Twins": "twins.png",
  "Cleveland Guardians": "indians.png",
  
  // American League West
  "Houston Astros": "astros.png",
  "Los Angeles Angels": "angels.png",
  "Oakland Athletics": "athletics.png",
  "Seattle Mariners": "mariners.png",
  "Texas Rangers": "rangers.png",
  
  // National League East
  "Atlanta Braves": "braves.png",
  "New York Mets": "mets.png",
  "Philadelphia Phillies": "phillies.png",
  "Washington Nationals": "nationals.png",
  "Miami Marlins": "marlins.png",
  
  // National League Central
  "Chicago Cubs": "cubs.png",
  "Milwaukee Brewers": "brewers.png",
  "St. Louis Cardinals": "cardinals.png",
  "Pittsburgh Pirates": "pirates.png",
  "Cincinnati Reds": "reds.png",
  
  // National League West
  "Los Angeles Dodgers": "dodgers.png",
  "San Francisco Giants": "giants.png",
  "San Diego Padres": "padres.png",
  "Arizona Diamondbacks": "diamondbacks.png",
  "Colorado Rockies": "rockies.png"
};

const getTeamLogo = (teamName) => {
  const logoFile = teamLogoMapping[teamName];
  return logoFile ? `/logos/MLB/${logoFile}` : null;
};

// Generate team colors based on team name (for visual variety)
function getTeamColors(teamName) {
  const hash = teamName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = hash % 360;
  return {
    primary: `hsl(${hue}, 70%, 50%)`,
    secondary: `hsl(${hue}, 70%, 40%)`,
    light: `hsl(${hue}, 70%, 60%)`
  };
}

// EPL team logo mapping
const eplTeamLogoMapping = {
  // Direct matches
  "Liverpool": "liverpool.png",
  "Bournemouth": "bournemouth.png", 
  "Aston Villa": "astonVilla.png",
  "Newcastle United": "newcastleUnited.png",
  "Fulham": "fulham.png",
  "Tottenham Hotspur": "tottenhamHotspur.png",
  "Burnley": "burnley.png",
  "West Ham United": "westHamUnited.png",
  "Manchester City": "manchesterCity.png",
  
  // Special cases
  "Brighton and Hove Albion": "brighton.png",
  "Wolverhampton Wanderers": "wolves.png", 
  "Sunderland": "sunderlandAfc.png",
  
  // Additional teams with logos available
  "Arsenal": "arsenal.png",
  "Chelsea": "chelsea.png", 
  "Manchester United": "manchesterUnited.png",
  "Leicester City": "leicesterCity.png",
  "Everton": "everton.png",
  "Crystal Palace": "crystalPalace.png",
  "Southampton": "southampton.png",
  
  // Historical/Championship teams (available but may not be in current EPL)
  "Cardiff City": "cardiffCity.png",
  "Huddersfield Town": "huddersfield.png",
  "Norwich City": "norwichCity.png",
  "Watford": "watford.png",
  "West Bromwich Albion": "westBromwichAlbion.png",
  "Stoke City": "stokeCity.png",
  "Swansea City": "swanseaCityAfc.png"
};

// NBA team logo mapping
const nbaTeamLogoMapping = {
  // Eastern Conference - Atlantic Division
  "Boston Celtics": "celtics.png",
  "Brooklyn Nets": "nets.png",
  "New York Knicks": "knicks.png",
  "Philadelphia 76ers": "76ers.png",
  "Toronto Raptors": "raptors.png",
  
  // Eastern Conference - Central Division
  "Chicago Bulls": "bulls.png",
  "Cleveland Cavaliers": "cavaliers.png",
  "Detroit Pistons": "pistons.png",
  "Indiana Pacers": "pacers.png",
  "Milwaukee Bucks": "bucks.png",
  
  // Eastern Conference - Southeast Division
  "Atlanta Hawks": "hawks.png",
  "Charlotte Hornets": "hornets.png",
  "Miami Heat": "heat.png",
  "Orlando Magic": "magic.png",
  "Washington Wizards": "wizards.png",
  
  // Western Conference - Northwest Division
  "Denver Nuggets": "nuggets.png",
  "Minnesota Timberwolves": "timberwolves.png",
  "Oklahoma City Thunder": "thunder.png",
  "Portland Trail Blazers": "trailBlazers.png",
  "Utah Jazz": "jazz.png",
  
  // Western Conference - Pacific Division
  "Golden State Warriors": "warriors.png",
  "Los Angeles Clippers": "clippers.png",
  "Los Angeles Lakers": "lakers.png",
  "Phoenix Suns": "suns.png",
  "Sacramento Kings": "kings.png",
  
  // Western Conference - Southwest Division
  "Dallas Mavericks": "mavericks.png",
  "Houston Rockets": "rockets.png",
  "Memphis Grizzlies": "grizzlies.png",
  "New Orleans Pelicans": "pelicans.png",
  "San Antonio Spurs": "spurs.png"
};

const getEplTeamLogo = (teamName) => {
  const logoFile = eplTeamLogoMapping[teamName];
  return logoFile ? `/logos/Premier_League/${logoFile}` : null;
};

const getNbaTeamLogo = (teamName) => {
  const logoFile = nbaTeamLogoMapping[teamName];
  return logoFile ? `/logos/NBA/${logoFile}` : null;
};

const getTeamInitials = (teamName) => {
  return teamName.split(' ').map(word => word[0]).join('').slice(0, 3);
};

function TopSoccerGamesCarousel() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  
  // Responsive hook for mobile detection
  const { isMobile } = useResponsive();
  
  // Touch/swipe handling for mobile
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(0); // Reset touch end
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && games.length > 1) {
      next(); // Swipe left = next
    }
    if (isRightSwipe && games.length > 1) {
      prev(); // Swipe right = previous
    }
  };

  useEffect(() => {
          fetch(`https://api.the-odds-api.com/v4/sports/baseball_mlb/odds/?apiKey=${APP_CONSTANTS.ODDS_API_KEY}&regions=us&markets=h2h,spreads,totals`)
      .then(res => res.json())
      .then(data => {
        const now = new Date();
        const soon = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000); // next 48 hours
        const upcomingGames = data
          .filter(game => {
            const gameTime = new Date(game.commence_time);
            return gameTime >= now && gameTime <= soon;
          })
          .sort((a, b) => new Date(a.commence_time) - new Date(b.commence_time))
          .slice(0, 5);
        setGames(upcomingGames);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const next = () => {
    if (isTransitioning || games.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent((prev) => (prev + 1) % games.length);
      setIsTransitioning(false);
    }, 300);
  };
  
  const prev = () => {
    if (isTransitioning || games.length <= 1) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrent((prev) => (prev - 1 + games.length) % games.length);
      setIsTransitioning(false);
    }, 300);
  };

  // Auto-advance carousel
  useEffect(() => {
    if (games.length > 1) {
      const interval = setInterval(() => {
        next();
      }, 5000); // Change slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [current, games.length, isTransitioning]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: tokens.spacing[4],
        padding: `${tokens.spacing[8]} 0`
      }}>
        <h2 style={{
          fontSize: tokens.typography.fontSize['3xl'],
          fontWeight: tokens.typography.fontWeight.bold,
          background: tokens.colors.gradients.glow,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: tokens.spacing[4]
        }}>
          Loading Today's Hottest Games...
        </h2>
        <div className="skeleton" style={{ 
          width: '100%',
          maxWidth: '1000px',
          height: '420px',
          borderRadius: tokens.borderRadius['2xl']
        }} />
      </div>
    );
  }
  
  if (!games.length) {
    return (
      <Card variant="gradient" padding="large">
        <div style={{ 
          textAlign: 'center',
          color: tokens.colors.neutral[400],
          padding: `${tokens.spacing[8]} 0`
        }}>
          <p style={{ fontSize: tokens.typography.fontSize.lg }}>
            No games found in the next 48 hours.
          </p>
        </div>
      </Card>
    );
  }

  const game = games[current];
  const homeColors = getTeamColors(game.home_team);
  const awayColors = getTeamColors(game.away_team);
  const gameDate = new Date(game.commence_time);

  const handleCardClick = () => {
    navigate(`/game/${game.id}`, { state: { game, sport: 'MLB' } });
  };

  return (
    <div style={{
      position: 'relative',
      padding: `${tokens.spacing[4]} 0`
    }}>
      {/* Section Title */}
      <div style={{ textAlign: 'center', marginBottom: tokens.spacing[8] }}>
        <h2 style={{
          fontSize: tokens.typography.fontSize['4xl'],
          fontWeight: tokens.typography.fontWeight.extrabold,
          background: tokens.colors.gradients.glow,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: tokens.spacing[2],
          filter: 'drop-shadow(0 0 40px rgba(34, 197, 94, 0.3))'
        }}>
          🔥 Today's Featured Matches
        </h2>
        <p style={{
          fontSize: tokens.typography.fontSize.lg,
          color: tokens.colors.neutral[400]
        }}>
          Place your bets on the most exciting games
        </p>
      </div>

      {/* Main Carousel Container */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing[6],
        position: 'relative',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Navigation Button Left */}
        <Button
          variant="ghost"
          size="medium"
          onClick={prev}
          disabled={games.length <= 1}
          className={isMobile ? responsiveStyles.hideOnMobile : ''}
          style={{
            padding: 0,
            minWidth: '50px',
            height: '50px',
            borderRadius: tokens.borderRadius.full,
            background: `${tokens.colors.neutral[800]}66`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${tokens.colors.neutral[700]}`,
            zIndex: 2,
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Button>

        {/* Main Featured Card */}
        <div 
          style={{
            flex: 1,
            position: 'relative',
            opacity: isTransitioning ? 0.8 : 1,
            transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)'
          }}
          onTouchStart={isMobile ? onTouchStart : undefined}
          onTouchMove={isMobile ? onTouchMove : undefined}
          onTouchEnd={isMobile ? onTouchEnd : undefined}
        >
          <div
            onClick={handleCardClick}
            style={{
              position: 'relative',
              background: `linear-gradient(135deg, ${tokens.colors.dark[800]} 0%, ${tokens.colors.dark[900]} 100%)`,
              borderRadius: tokens.borderRadius['2xl'],
              overflow: 'hidden',
              cursor: 'pointer',
              border: `2px solid ${tokens.colors.neutral[700]}`,
              boxShadow: `0 20px 40px rgba(0, 0, 0, 0.5), 0 0 60px ${tokens.colors.primary[900]}33`,
              minHeight: '380px',
              transform: 'translateZ(0)',
              transition: 'all 300ms ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 24px 48px rgba(0, 0, 0, 0.6), 0 0 80px ${tokens.colors.primary[900]}44`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 20px 40px rgba(0, 0, 0, 0.5), 0 0 60px ${tokens.colors.primary[900]}33`;
            }}
          >
            {/* Background Effects */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(135deg, ${homeColors.primary}22 0%, transparent 50%, ${awayColors.primary}22 100%)`,
              opacity: 0.3
            }} />
            
            {/* Animated Gradient Orbs */}
            <div style={{
              position: 'absolute',
              top: '-30%',
              left: '-10%',
              width: '320px',
              height: '320px',
              background: homeColors.primary,
              borderRadius: '50%',
              opacity: 0.15,
              filter: 'blur(100px)',
              animation: 'float 8s ease-in-out infinite'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30%',
              right: '-10%',
              width: '320px',
              height: '320px',
              background: awayColors.primary,
              borderRadius: '50%',
              opacity: 0.15,
              filter: 'blur(100px)',
              animation: 'float 8s ease-in-out infinite reverse'
            }} />

            {/* Content */}
            <div style={{ position: 'relative', zIndex: 1, padding: tokens.spacing[8] }}>
              {/* League Info - Top Left */}
              <div style={{ 
                position: 'absolute',
                top: tokens.spacing[4],
                left: tokens.spacing[6],
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacing[2],
                background: `${tokens.colors.dark[800]}88`,
                backdropFilter: 'blur(10px)',
                borderRadius: tokens.borderRadius.lg,
                padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                border: `1px solid ${tokens.colors.neutral[800]}`
              }}>
                <img 
                  src="/logos/MLB/_MLB_logo.png" 
                  alt="MLB"
                  style={{
                    width: '20px',
                    height: '20px',
                    objectFit: 'contain'
                  }}
                />
                <span style={{
                  color: tokens.colors.neutral[300],
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.semibold
                }}>
                  MLB
                </span>
              </div>

              {/* Match Header */}
              <div style={{ 
                textAlign: 'center',
                marginBottom: tokens.spacing[8]
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: tokens.spacing[2],
                  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                  background: `${tokens.colors.accent[900]}33`,
                  border: `1px solid ${tokens.colors.accent[700]}`,
                  borderRadius: tokens.borderRadius.full,
                  marginBottom: tokens.spacing[4]
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    background: tokens.colors.accent[400],
                    borderRadius: '50%',
                    animation: 'pulse 2s ease-in-out infinite'
                  }} />
                  <span style={{
                    color: tokens.colors.accent[400],
                    fontSize: tokens.typography.fontSize.sm,
                    fontWeight: tokens.typography.fontWeight.semibold,
                    letterSpacing: '0.05em'
                  }}>
                    LIVE ODDS
                  </span>
                </div>

                <div style={{
                  fontSize: tokens.typography.fontSize.lg,
                  color: tokens.colors.neutral[400],
                  marginBottom: tokens.spacing[3]
                }}>
                  {gameDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  {' • '}
                  {gameDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </div>

                {/* Teams Display */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  gap: tokens.spacing[6],
                  marginBottom: tokens.spacing[6]
                }}>
                  {/* Home Team */}
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: tokens.borderRadius.lg,
                      margin: '0 auto',
                      marginBottom: tokens.spacing[3],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: tokens.typography.fontSize['2xl'],
                      fontWeight: tokens.typography.fontWeight.bold,
                      color: 'white'
                    }}>
                      {(getTeamLogo(game.home_team) || getEplTeamLogo(game.home_team) || getNbaTeamLogo(game.home_team)) ? (
                        <img
                          src={getTeamLogo(game.home_team) || getEplTeamLogo(game.home_team) || getNbaTeamLogo(game.home_team)}
                          alt={game.home_team}
                          style={{
                            width: '120px',
                            height: '120px',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{
                        display: (getTeamLogo(game.home_team) || getEplTeamLogo(game.home_team) || getNbaTeamLogo(game.home_team)) ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${homeColors.primary} 0%, ${homeColors.secondary} 100%)`,
                        borderRadius: tokens.borderRadius.lg
                      }}>
                        {getTeamInitials(game.home_team)}
                      </div>
                    </div>
                    <h3 style={{
                      fontSize: tokens.typography.fontSize['2xl'],
                      fontWeight: tokens.typography.fontWeight.bold,
                      color: tokens.colors.neutral[100],
                      marginBottom: tokens.spacing[1]
                    }}>
                      {game.home_team}
                    </h3>
                    <div style={{
                      fontSize: tokens.typography.fontSize.sm,
                      color: tokens.colors.neutral[500]
                    }}>
                      HOME
                    </div>
                  </div>

                  {/* VS */}
                  <div style={{
                    padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
                    background: `${tokens.colors.neutral[800]}66`,
                    borderRadius: tokens.borderRadius.lg,
                    fontSize: tokens.typography.fontSize.xl,
                    fontWeight: tokens.typography.fontWeight.bold,
                    color: tokens.colors.neutral[400],
                    border: `1px solid ${tokens.colors.neutral[700]}`
                  }}>
                    VS
                  </div>

                  {/* Away Team */}
                  <div style={{ textAlign: 'left' }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: tokens.borderRadius.lg,
                      margin: '0 auto',
                      marginBottom: tokens.spacing[3],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: tokens.typography.fontSize['2xl'],
                      fontWeight: tokens.typography.fontWeight.bold,
                      color: 'white'
                    }}>
                      {(getTeamLogo(game.away_team) || getEplTeamLogo(game.away_team) || getNbaTeamLogo(game.away_team)) ? (
                        <img
                          src={getTeamLogo(game.away_team) || getEplTeamLogo(game.away_team) || getNbaTeamLogo(game.away_team)}
                          alt={game.away_team}
                          style={{
                            width: '120px',
                            height: '120px',
                            objectFit: 'contain'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{
                        display: (getTeamLogo(game.away_team) || getEplTeamLogo(game.away_team) || getNbaTeamLogo(game.away_team)) ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${awayColors.primary} 0%, ${awayColors.secondary} 100%)`,
                        borderRadius: tokens.borderRadius.lg
                      }}>
                        {getTeamInitials(game.away_team)}
                      </div>
                    </div>
                    <h3 style={{
                      fontSize: tokens.typography.fontSize['2xl'],
                      fontWeight: tokens.typography.fontWeight.bold,
                      color: tokens.colors.neutral[100],
                      marginBottom: tokens.spacing[1]
                    }}>
                      {game.away_team}
                    </h3>
                    <div style={{
                      fontSize: tokens.typography.fontSize.sm,
                      color: tokens.colors.neutral[500]
                    }}>
                      AWAY
                    </div>
                  </div>
                </div>

                {/* Odds Display */}
                {game.bookmakers && game.bookmakers.length > 0 && (
                  <div style={{
                    background: `${tokens.colors.neutral[800]}44`,
                    backdropFilter: 'blur(10px)',
                    borderRadius: tokens.borderRadius.xl,
                    padding: tokens.spacing[6],
                    border: `1px solid ${tokens.colors.neutral[700]}`
                  }}>
                    <h4 style={{
                      fontSize: tokens.typography.fontSize.base,
                      fontWeight: tokens.typography.fontWeight.semibold,
                      color: tokens.colors.primary[400],
                      marginBottom: tokens.spacing[4],
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Moneyline Odds
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${game.bookmakers[0].markets[0].outcomes.length}, 1fr)`,
                      gap: tokens.spacing[3]
                    }}>
                      {/* Sort outcomes to put home team first (left), then away team (right) */}
                      {game.bookmakers[0].markets[0].outcomes
                        .sort((a, b) => {
                          if (a.name === game.home_team) return -1;
                          if (b.name === game.home_team) return 1;
                          if (a.name === game.away_team) return -1;
                          if (b.name === game.away_team) return 1;
                          return 0;
                        })
                        .map((outcome) => (
                        <div
                          key={outcome.name}
                          style={{
                            background: `${tokens.colors.dark[900]}66`,
                            padding: tokens.spacing[4],
                            borderRadius: tokens.borderRadius.lg,
                            border: `1px solid ${tokens.colors.neutral[700]}`,
                            textAlign: 'center',
                            transition: 'all 200ms ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = `${tokens.colors.primary[900]}44`;
                            e.currentTarget.style.borderColor = tokens.colors.primary[700];
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = `${tokens.colors.dark[900]}66`;
                            e.currentTarget.style.borderColor = tokens.colors.neutral[700];
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <div style={{
                            fontSize: tokens.typography.fontSize.sm,
                            color: tokens.colors.neutral[400],
                            marginBottom: tokens.spacing[1]
                          }}>
                            {outcome.name}
                          </div>
                          <div style={{
                            fontSize: tokens.typography.fontSize['2xl'],
                            fontWeight: tokens.typography.fontWeight.bold,
                            background: tokens.colors.gradients.accent,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            filter: 'drop-shadow(0 0 10px rgba(34, 197, 94, 0.3))'
                          }}>
                            {decimalToAmerican(outcome.price)}
                          </div>
                        </div>
                      ))}
                    </div>
        </div>
      )}

                {/* CTA */}
                <div style={{
                  marginTop: tokens.spacing[6],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: tokens.spacing[3]
                }}>
                  <div style={{
                    padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
                    background: tokens.colors.gradients.primary,
                    borderRadius: tokens.borderRadius.full,
                    color: 'white',
                    fontWeight: tokens.typography.fontWeight.semibold,
                    fontSize: tokens.typography.fontSize.base,
                    display: 'flex',
                    alignItems: 'center',
                    gap: tokens.spacing[2],
                    boxShadow: `0 10px 30px ${tokens.colors.primary[500]}66`,
                    animation: 'pulse 2s ease-in-out infinite'
                  }}>
                    <span>Place Your Bet</span>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M5 10H15M15 10L10 5M15 10L10 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Button Right */}
        <Button
          variant="ghost"
          size="medium"
          onClick={next}
          disabled={games.length <= 1}
          className={isMobile ? responsiveStyles.hideOnMobile : ''}
          style={{
            padding: 0,
            minWidth: '50px',
            height: '50px',
            borderRadius: tokens.borderRadius.full,
            background: `${tokens.colors.neutral[800]}66`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${tokens.colors.neutral[700]}`,
            zIndex: 2,
            display: isMobile ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Button>
      </div>

      {/* Game Indicators */}
      {games.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: tokens.spacing[2],
          marginTop: tokens.spacing[8]
        }}>
          {games.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrent(index)}
              style={{
                width: index === current ? '40px' : '12px',
                height: '12px',
                borderRadius: '6px',
                border: 'none',
                background: index === current 
                  ? tokens.colors.gradients.primary
                  : tokens.colors.neutral[700],
                transition: 'all 300ms ease',
                cursor: 'pointer',
                boxShadow: index === current 
                  ? `0 0 20px ${tokens.colors.primary[500]}66`
                  : 'none'
              }}
              aria-label={`Go to game ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -30px) scale(1.1); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}

export default TopSoccerGamesCarousel; 